/**
 * Eldstad conversion-ladder client (ELD-47 contract, deployed by ELD-48)
 * ----------------------------------------------------------------------
 * Deployed as a theme asset, not inside a theme app extension. ELD-60 measured
 * the extension route and rejected it: the collection grid section declares no
 * blocks, so the heart needs DOM injection either way, and the extension would
 * have cost a hosted backend for nothing. See
 * eldstad-ops/documentations/favourites-delivery-decision-record.md
 *
 * Source of truth is eldstad-ops/instrumentation/eldstad-ladder-client.js.
 * Edit there, copy here. Never the other way round.
 *
 * WHAT THIS FILE DOES NOT CONTAIN: any PostHog key. This publishes onto
 * Shopify's own customer-events bus and stops there. The Shopify custom pixel
 * in the admin subscribes to those events and forwards them to PostHog, and it
 * is the pixel that holds the project token.
 *
 * That split is why the site can go live before the PostHog account exists.
 * With no pixel connected the events are published and nobody is listening, so
 * nothing breaks and nothing is stored. When the account arrives, the token
 * goes into the pixel in the admin and collection starts with no theme change
 * and no redeploy. The only cost of shipping early is that events fired in the
 * gap are not recovered: they were never collected, not buffered.
 *
 * Why a helper rather than each ticket calling Shopify.analytics.publish
 * directly: the payload shape is the thing that has to stay stable across five
 * tickets built weeks apart. One funnel with five slightly different payloads
 * is a funnel nobody trusts.
 *
 * Shopify requires published custom events to be namespaced, hence `eldstad:`.
 * Standard events cannot be published, only subscribed to, so nothing here
 * fakes a page_viewed.
 */

(function (window) {
  'use strict';

  function publish(name, data) {
    // The theme app extension may load before Shopify's analytics object. Fail
    // quiet: a dropped analytics event must never break a favourite.
    var shopify = window.Shopify;
    if (!shopify || !shopify.analytics || !shopify.analytics.publish) return;
    try {
      shopify.analytics.publish('eldstad:' + name, data || {});
    } catch (err) {
      /* never let instrumentation take the feature down with it */
    }
  }

  var EldstadLadder = {
    /**
     * Rung 2. The yellow bag: the first micro-commitment.
     *
     * `product_handle` is the required identity and the join key for the whole
     * ladder. On a collection grid read it from `card.dataset.product` on
     * `.card-wrapper`. The numeric id is also reachable there, but only inside
     * a `.col_clct` span owned by a third-party app, so prefer the handle: it
     * is the theme's own attribute and the PIM keys on it.
     *
     * `price` and `category` are optional but you should send them. The grid
     * renders the price in `.price-item` on each card. Note it is not in the
     * server HTML, so read it after the theme has rendered rather than at
     * script start. A favourite that still arrives without one recovers its
     * band by joining to product_viewed on the same handle.
     * @param {{product_handle: string, list_size: number, product_id?: string, price?: number, category?: string, postcode?: string}} data
     */
    favouriteAdded: function (data) {
      publish('favourite_added', data);
    },

    /**
     * Off-ladder, but a step back is signal too: a product that gets saved and
     * then dropped is telling you something a counter of saves alone hides.
     * @param {{product_handle: string, list_size: number}} data
     */
    favouriteRemoved: function (data) {
      publish('favourite_removed', data);
    },

    /**
     * Rung 3. Explorer becomes a cold lead. The share_id must be the same id
     * that goes into the share link, or the accept side cannot be joined back.
     * @param {{share_id: string, product_handles: string[], list_size: number}} data
     */
    listShared: function (data) {
      publish('list_shared', data);
    },

    /**
     * Rung 4. Fired on the recipient's device when a shared list opens. This is
     * a DIFFERENT visitor from the sharer, by design: see the "share is two
     * funnels" note in the decision record before reading any drop-off here.
     * @param {{share_id: string, product_handles: string[]}} data
     */
    shareAccepted: function (data) {
      publish('share_accepted', data);
    },

    /**
     * Rung 5. Comparison is the reason the session gave for why a consumer
     * would want favourites at all, so its rate is the test of that premise.
     * @param {{product_handles: string[]}} data
     */
    comparisonRun: function (data) {
      publish('comparison_run', data);
    },

    /**
     * Rung 6. Ranked, never scored: the session rejected sliders because
     * everyone maxes them. ranked_values is an ordered array, best first.
     * @param {{ranked_values: string[]}} data
     */
    softValuesRanked: function (data) {
      publish('soft_values_ranked', data);
    },

    /**
     * Rung 7. Postcode may arrive here. Pass it raw; the pixel coarsens it to
     * three digits before anything leaves the browser.
     * @param {{product_handle?: string, install_check_result?: string, postcode?: string}} data
     */
    installCheckCompleted: function (data) {
      publish('install_check_completed', data);
    },

    /**
     * Rung 8. Semi-warm: an enquiry submitted, per the session's lead
     * temperature model.
     * @param {{product_handles?: string[], postcode?: string}} data
     */
    bookingSubmitted: function (data) {
      publish('booking_submitted', data);
    },

    /**
     * Rung 9 (ELD-152). The prisindikation was entered, and from where. Not a
     * step on the old ladder but the mouth of the next one: everything after
     * it happens inside the flow, which counts itself.
     *
     * `source` is the place on the site, in the words a person would use:
     * meny, startsida, footer, produktsida. `product_handle` only when the
     * link carried one, which the product page's button does.
     * @param {{source: string, product_handle?: string}} data
     */
    prisindikationOpened: function (data) {
      publish('prisindikation_opened', data);
    }
  };

  window.EldstadLadder = EldstadLadder;
})(window);
